'use client';

import { useState, useCallback, useMemo } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { token } from '@terra/ui-styled-system';
import {
  Text,
  IconButton,
  Button,
  Tooltip,
  Badge,
  LegacyDialog,
  Input,
  Textarea,
} from '@terra/ui-design-system';
import {
  Bookmark01Icon,
  Copy01Icon,
  Edit02Icon,
  SquareLock01Icon,
  PlayIcon,
  Add01Icon,
  FloppyDiskIcon,
  Delete02Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import {
  useFilterPresetsStore,
  useAllPresets,
} from '../../store/filterPresetsStore';
import { useTabScopedRequestsFilters } from '../../store/tab/requestsTabScopedFilterStore';
import {
  validateTql,
  tokenizeForHighlight,
  type HighlightToken,
} from '../../utils/tql';
import type { FilterPreset } from '../../utils/tql';

function getTokenColor(type: HighlightToken['type']): string {
  switch (type) {
    case 'namespace':
    case 'field':
      return token('colors.sky.11');
    case 'operator':
      return token('colors.yellow.11');
    case 'value':
      return token('colors.green.11');
    case 'keyword':
      return token('colors.purple.11');
    case 'preset':
      return token('colors.crimson.11');
    case 'paren':
      return token('colors.fg.muted');
    case 'error':
      return token('colors.tomato.11');
    case 'text':
    default:
      return token('colors.fg.default');
  }
}

type FilterPresetsManagerProps = {
  onApplyPreset: (query: string) => void;
};

export function FilterPresetsManager({
  onApplyPreset,
}: FilterPresetsManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<FilterPreset | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const presets = useAllPresets();
  const { createPreset, updatePreset, deletePreset, duplicatePreset } =
    useFilterPresetsStore();
  const builtInPresets = presets.filter((p) => p.isBuiltIn);
  const userPresets = presets.filter((p) => !p.isBuiltIn);
  const handleApply = useCallback(
    (preset: FilterPreset) => {
      onApplyPreset(preset.query);
      setIsOpen(false);
    },
    [onApplyPreset],
  );
  const handleDuplicate = useCallback(
    (preset: FilterPreset) => {
      const newPreset = duplicatePreset(preset.id);

      if (newPreset) {
        setEditingPreset(newPreset);
      }
    },
    [duplicatePreset],
  );
  const handleDelete = useCallback(
    (preset: FilterPreset) => {
      if (preset.isBuiltIn) return;
      deletePreset(preset.id);
    },
    [deletePreset],
  );
  const handleEdit = useCallback((preset: FilterPreset) => {
    if (preset.isBuiltIn) return;
    setEditingPreset(preset);
  }, []);
  const handleCreate = useCallback(() => {
    setIsCreating(true);
    setEditingPreset({
      id: '',
      name: '',
      alias: '',
      description: '',
      query: '',
      isBuiltIn: false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }, []);
  const handleSave = useCallback(
    (preset: FilterPreset) => {
      if (isCreating) {
        createPreset({
          name: preset.name,
          alias: preset.alias,
          description: preset.description,
          query: preset.query,
          isBuiltIn: false,
        });
      } else {
        updatePreset(preset.id, {
          name: preset.name,
          alias: preset.alias,
          description: preset.description,
          query: preset.query,
        });
      }

      setEditingPreset(null);
      setIsCreating(false);
    },
    [isCreating, createPreset, updatePreset],
  );
  const handleCancel = useCallback(() => {
    setEditingPreset(null);
    setIsCreating(false);
  }, []);

  return (
    <>
      <Tooltip title="Manage filter presets">
        <IconButton
          aria-label="Filter presets"
          size="xs"
          variant="ghost"
          onClick={() => setIsOpen(true)}
        >
          <Bookmark01Icon size={14} />
        </IconButton>
      </Tooltip>

      <LegacyDialog.Root
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
        lazyMount
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="600px" p="0" overflow="hidden">
            {/* Header */}
            <HStack
              justifyContent="space-between"
              alignItems="center"
              px="5"
              py="4"
              borderBottomWidth="1px"
              borderColor="border.default"
              bg="bg.subtle"
            >
              <HStack gap="2" alignItems="center">
                <Bookmark01Icon size={18} />
                <LegacyDialog.Title>Filter Presets</LegacyDialog.Title>
              </HStack>
              <LegacyDialog.CloseTrigger asChild>
                <IconButton aria-label="Close" variant="ghost" size="sm">
                  <Cancel01Icon size={16} />
                </IconButton>
              </LegacyDialog.CloseTrigger>
            </HStack>

            {/* Body */}
            <Box p="5">
              {editingPreset ? (
                <PresetEditor
                  preset={editingPreset}
                  isCreating={isCreating}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              ) : (
                <VStack gap="4" alignItems="stretch">
                  {/* Create button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCreate}
                    w="full"
                  >
                    <Add01Icon size={14} />
                    Create New Preset
                  </Button>

                  {/* Scrollable presets list */}
                  <Box maxH="500px" overflowY="auto" mx="-2" px="2">
                    <VStack gap="4" alignItems="stretch">
                      {/* User presets */}
                      {userPresets.length > 0 && (
                        <VStack gap="2" alignItems="stretch">
                          <Text
                            fontSize="xs"
                            fontWeight="semibold"
                            color="fg.muted"
                            textTransform="uppercase"
                            letterSpacing="wide"
                          >
                            Your Presets
                          </Text>
                          {userPresets.map((preset) => (
                            <PresetItem
                              key={preset.id}
                              preset={preset}
                              onApply={handleApply}
                              onEdit={handleEdit}
                              onDuplicate={handleDuplicate}
                              onDelete={handleDelete}
                            />
                          ))}
                        </VStack>
                      )}

                      {/* Built-in presets */}
                      <VStack gap="2" alignItems="stretch">
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color="fg.muted"
                          textTransform="uppercase"
                          letterSpacing="wide"
                        >
                          Built-in Presets
                        </Text>
                        {builtInPresets.map((preset) => (
                          <PresetItem
                            key={preset.id}
                            preset={preset}
                            onApply={handleApply}
                            onEdit={handleEdit}
                            onDuplicate={handleDuplicate}
                            onDelete={handleDelete}
                          />
                        ))}
                      </VStack>
                    </VStack>
                  </Box>
                </VStack>
              )}
            </Box>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </>
  );
}

type PresetItemProps = {
  preset: FilterPreset;
  onApply: (preset: FilterPreset) => void;
  onEdit: (preset: FilterPreset) => void;
  onDuplicate: (preset: FilterPreset) => void;
  onDelete: (preset: FilterPreset) => void;
};

function PresetItem({
  preset,
  onApply,
  onEdit,
  onDuplicate,
  onDelete,
}: PresetItemProps) {
  return (
    <Box
      p="3"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      bg="bg.default"
      _hover={{ bg: 'bg.subtle', borderColor: 'border.muted' }}
      transition="all 0.15s ease"
    >
      <VStack gap="2" alignItems="stretch">
        {/* Header row */}
        <HStack gap="2" alignItems="center" flexWrap="wrap">
          <Text fontSize="sm" fontWeight="medium">
            {preset.name}
          </Text>
          <Badge size="sm" variant="outline">
            preset:{preset.alias}
          </Badge>
          {preset.isBuiltIn && (
            <Tooltip title="Built-in preset (read-only)">
              <Box color="fg.muted">
                <SquareLock01Icon size={12} />
              </Box>
            </Tooltip>
          )}
        </HStack>

        {/* Description */}
        {preset.description && (
          <Text fontSize="xs" color="fg.muted" lineClamp={1}>
            {preset.description}
          </Text>
        )}

        {/* Query preview */}
        <Box
          p="2"
          bg="bg.canvas"
          borderRadius="sm"
          borderWidth="1px"
          borderColor="border.subtle"
        >
          <HighlightedQuery query={preset.query} />
        </Box>

        {/* Action buttons - horizontal */}
        <HStack gap="1" justify="flex-end">
          <Tooltip title="Apply filter">
            <IconButton
              aria-label="Apply"
              size="xs"
              variant="ghost"
              onClick={() => onApply(preset)}
            >
              <PlayIcon size={14} />
            </IconButton>
          </Tooltip>

          {!preset.isBuiltIn && (
            <Tooltip title="Edit preset">
              <IconButton
                aria-label="Edit"
                size="xs"
                variant="ghost"
                onClick={() => onEdit(preset)}
              >
                <Edit02Icon size={14} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Duplicate preset">
            <IconButton
              aria-label="Duplicate"
              size="xs"
              variant="ghost"
              onClick={() => onDuplicate(preset)}
            >
              <Copy01Icon size={14} />
            </IconButton>
          </Tooltip>

          {!preset.isBuiltIn && (
            <Tooltip title="Delete preset">
              <IconButton
                aria-label="Delete"
                size="xs"
                variant="ghost"
                onClick={() => onDelete(preset)}
              >
                <Delete02Icon size={14} />
              </IconButton>
            </Tooltip>
          )}
        </HStack>
      </VStack>
    </Box>
  );
}

type PresetEditorProps = {
  preset: FilterPreset;
  isCreating: boolean;
  onSave: (preset: FilterPreset) => void;
  onCancel: () => void;
};

function PresetEditor({
  preset,
  isCreating,
  onSave,
  onCancel,
}: PresetEditorProps) {
  const [name, setName] = useState(preset.name);
  const [alias, setAlias] = useState(preset.alias);
  const [description, setDescription] = useState(preset.description || '');
  const [query, setQuery] = useState(preset.query);
  const { tqlQuery: currentQuery } = useTabScopedRequestsFilters();
  const validation = validateTql(query);
  const canSave =
    name.trim() !== '' &&
    alias.trim() !== '' &&
    query.trim() !== '' &&
    validation.valid;
  const handleSave = useCallback(() => {
    if (!canSave) return;
    onSave({
      ...preset,
      name: name.trim(),
      alias: alias.trim().toLowerCase().replace(/\s+/g, '-'),
      description: description.trim(),
      query: query.trim(),
    });
  }, [canSave, name, alias, description, query, preset, onSave]);
  const handleUseCurrentQuery = useCallback(() => {
    if (currentQuery) {
      setQuery(currentQuery);
    }
  }, [currentQuery]);
  const queryTokens = useMemo(() => tokenizeForHighlight(query), [query]);

  return (
    <VStack gap="5" alignItems="stretch">
      {/* Form header */}
      <HStack justify="space-between" alignItems="center">
        <Text fontSize="md" fontWeight="semibold">
          {isCreating ? 'Create New Preset' : 'Edit Preset'}
        </Text>
        {currentQuery && (
          <Button variant="outline" size="xs" onClick={handleUseCurrentQuery}>
            Use Current Query
          </Button>
        )}
      </HStack>

      <VStack gap="4" alignItems="stretch">
        {/* Name & Alias row */}
        <HStack gap="3" alignItems="flex-start">
          <Box flex="1">
            <Text fontSize="xs" fontWeight="medium" color="fg.muted" mb="1.5">
              Name
            </Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Hide Polling Requests"
              size="sm"
            />
          </Box>
          <Box w="180px" flexShrink={0}>
            <Text fontSize="xs" fontWeight="medium" color="fg.muted" mb="1.5">
              Alias
            </Text>
            <Input
              value={alias}
              onChange={(e) =>
                setAlias(e.target.value.toLowerCase().replace(/\s+/g, '-'))
              }
              placeholder="hide-polling"
              size="sm"
              fontFamily="mono"
            />
          </Box>
        </HStack>

        {/* Description */}
        <Box>
          <Text fontSize="xs" fontWeight="medium" color="fg.muted" mb="1.5">
            Description{' '}
            <Text as="span" color="fg.subtle">
              (optional)
            </Text>
          </Text>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this filter do?"
            size="sm"
          />
        </Box>

        {/* Query */}
        <Box>
          <HStack justify="space-between" mb="1.5">
            <Text fontSize="xs" fontWeight="medium" color="fg.muted">
              Query
            </Text>
            {query && !validation.valid && (
              <Text fontSize="xs" color="fg.error">
                {validation.error}
              </Text>
            )}
          </HStack>

          <Textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='e.g., req.path.ncont("/prices") AND req.path.ncont("/poll")'
            rows={2}
            fontFamily="mono"
            fontSize="sm"
            borderColor={
              query && !validation.valid ? 'border.error' : undefined
            }
          />

          {/* Highlighted query preview */}
          {query && (
            <Box
              mt="2"
              p="3"
              bg="bg.canvas"
              borderRadius="md"
              borderWidth="1px"
              borderColor={
                query && !validation.valid ? 'border.error' : 'border.default'
              }
            >
              <Text fontSize="xs" fontWeight="medium" color="fg.muted" mb="1">
                Preview
              </Text>
              <Box
                fontFamily="mono"
                fontSize="sm"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {queryTokens.map((highlightToken, idx) => (
                  <Box
                    as="span"
                    key={idx}
                    style={{ color: getTokenColor(highlightToken.type) }}
                  >
                    {highlightToken.text}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </VStack>

      {/* Footer */}
      <HStack
        gap="2"
        justify="flex-end"
        pt="3"
        borderTopWidth="1px"
        borderColor="border.default"
      >
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!canSave}>
          {isCreating ? 'Create Preset' : 'Save Changes'}
        </Button>
      </HStack>
    </VStack>
  );
}

function HighlightedQuery({ query }: { query: string }) {
  const tokens = useMemo(() => tokenizeForHighlight(query), [query]);

  return (
    <Box
      fontSize="xs"
      fontFamily="mono"
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      w="full"
    >
      {tokens.map((highlightToken, idx) => (
        <Box
          as="span"
          key={idx}
          style={{ color: getTokenColor(highlightToken.type) }}
        >
          {highlightToken.text}
        </Box>
      ))}
    </Box>
  );
}

export function QuickSavePreset() {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [alias, setAlias] = useState('');
  const { tqlQuery } = useTabScopedRequestsFilters();
  const { createPreset } = useFilterPresetsStore();
  const validation = validateTql(tqlQuery);
  const canSave =
    name.trim() !== '' &&
    alias.trim() !== '' &&
    tqlQuery.trim() !== '' &&
    validation.valid;
  const handleSave = useCallback(() => {
    if (!canSave) return;
    createPreset({
      name: name.trim(),
      alias: alias.trim().toLowerCase().replace(/\s+/g, '-'),
      description: '',
      query: tqlQuery.trim(),
      isBuiltIn: false,
    });
    setName('');
    setAlias('');
    setIsOpen(false);
  }, [canSave, name, alias, tqlQuery, createPreset]);
  const handleOpen = useCallback(() => {
    setName('');
    setAlias('');
    setIsOpen(true);
  }, []);

  if (!tqlQuery.trim() || !validation.valid) {
    return null;
  }

  return (
    <>
      <Tooltip title="Save current query as preset">
        <IconButton
          aria-label="Quick save preset"
          size="xs"
          variant="ghost"
          onClick={handleOpen}
        >
          <FloppyDiskIcon size={14} />
        </IconButton>
      </Tooltip>

      <LegacyDialog.Root
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
        lazyMount
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="420px" p="0" overflow="hidden">
            {/* Header */}
            <HStack
              justifyContent="space-between"
              alignItems="center"
              px="5"
              py="4"
              borderBottomWidth="1px"
              borderColor="border.default"
              bg="bg.subtle"
            >
              <HStack gap="2" alignItems="center">
                <FloppyDiskIcon size={18} />
                <LegacyDialog.Title>Quick Save Preset</LegacyDialog.Title>
              </HStack>
              <LegacyDialog.CloseTrigger asChild>
                <IconButton aria-label="Close" variant="ghost" size="sm">
                  <Cancel01Icon size={16} />
                </IconButton>
              </LegacyDialog.CloseTrigger>
            </HStack>

            {/* Body */}
            <VStack gap="4" p="5" alignItems="stretch">
              {/* Name & Alias */}
              <HStack gap="3" alignItems="flex-start">
                <Box flex="1">
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    color="fg.muted"
                    mb="1.5"
                  >
                    Name
                  </Text>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Hide Polling"
                    size="sm"
                    autoFocus
                  />
                </Box>
                <Box w="140px" flexShrink={0}>
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    color="fg.muted"
                    mb="1.5"
                  >
                    Alias
                  </Text>
                  <Input
                    value={alias}
                    onChange={(e) =>
                      setAlias(
                        e.target.value.toLowerCase().replace(/\s+/g, '-'),
                      )
                    }
                    placeholder="hide-poll"
                    size="sm"
                    fontFamily="mono"
                  />
                </Box>
              </HStack>

              {/* Query preview */}
              <Box
                p="3"
                bg="bg.canvas"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.default"
              >
                <Text fontSize="xs" fontWeight="medium" color="fg.muted" mb="1">
                  Query
                </Text>
                <Box
                  fontFamily="mono"
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                >
                  <HighlightedQuery query={tqlQuery} />
                </Box>
              </Box>
            </VStack>

            {/* Footer */}
            <HStack
              gap="2"
              justify="flex-end"
              px="5"
              py="4"
              borderTopWidth="1px"
              borderColor="border.default"
              bg="bg.subtle"
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={!canSave}>
                Save Preset
              </Button>
            </HStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </>
  );
}
